import { useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, MailCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDrop, MAX_FILES } from "./FileDrop";
import { Captcha, CAPTCHA_MSG, RecaptchaNotice } from "./Captcha";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const INQUIRY_OPTIONS = [
  "General Inquiries",
  "Partnerships & Business Opportunities",
  "Guest Appearances",
  "Events & Premieres Invitations",
  "Press & Media Inquiries",
  "Usage Rights & Licencing Inquiries",
  "Other",
];
const FIELD = "w-full border bg-white/60 px-4 py-3.5 text-base text-black placeholder:text-[#8a8580] outline-none transition-shadow focus:ring-4 focus:ring-[#E62314]/15";
const EMPTY = { full_name: "", email: "", inquiry: "", other: "", message: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (f) => {
  const e = {};
  if (!f.full_name.trim()) e.full_name = "Please enter your full name.";
  if (!EMAIL_RE.test(f.email.trim())) e.email = "Please enter a valid email address.";
  if (!f.inquiry) e.inquiry = "Please choose what your inquiry is about.";
  if (f.inquiry === "Other" && !f.other.trim()) e.other = "Please tell us what it's about.";
  if (!f.message.trim()) e.message = "Please write a message.";
  return e;
};

const Field = ({ id, label, error, required = true, children }) => (
  <div className="flex flex-col">
    <label htmlFor={id} className="mb-3 text-sm font-bold">
      {label}{required && <span className="ml-1 text-[#E62314]" aria-hidden>*</span>}
    </label>
    {children}
    {error && <p data-testid={`${id}-error`} className="mt-2 text-sm font-semibold text-[#C21B0E]">{error}</p>}
  </div>
);

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const captcha = useRef(null);
  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };
  const cls = (k) => `${FIELD} ${errors[k] ? "border-2 border-[#C21B0E]" : "border-[#E62314]"}`;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) {
      setError("Please fill in the highlighted fields above.");
      const first = ["full_name", "email", "inquiry", "other", "kind", "ref", "message"].find((k) => errs[k]);
      const ids = { full_name: "contact-name", email: "contact-email", inquiry: "contact-inquiry", other: "contact-other", kind: "contact-kind", ref: "contact-ref", message: "contact-message" };
      const el = document.getElementById(ids[first]);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.focus({ preventScroll: true });
      return;
    }
    if (!token) return setError(CAPTCHA_MSG);
    setLoading(true);
    try {
      await axios.post(`${API}/recaptcha/verify`, { token }).catch(() => {
        throw new Error("captcha");
      });
      const sub = new FormData();
      sub.append("name", form.full_name.trim()); sub.append("email", form.email.trim()); sub.append("category", form.inquiry);
      sub.append("topic", form.inquiry === "Other" ? form.other.trim() : "");
      sub.append("message", form.message.trim());
      files.forEach((f) => sub.append("files", f));
      await axios.post(`${API}/submissions`, sub).catch(() => { throw new Error("queue"); });
      setSent({ email: form.email.trim() });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.message === "captcha" ? "The robot check failed or expired. Please tick it again." : "Sorry, your message couldn't be sent. Please try again.");
    } finally {
      captcha.current?.reset();
      setToken("");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      {sent ? (
        <motion.div key="sent" data-testid="contact-success-box" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="bg-[#ECEAE6] px-8 py-10 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#0A0A0A] text-white"><MailCheck className="h-6 w-6" /></span>
          <p className="mt-5 font-display text-3xl font-extrabold tracking-tight">Confirmation email sent</p>
          <p className="mt-3 text-base text-[#3a3836]">We've sent a confirmation email to</p>
          <p className="mt-1 break-all text-base font-bold" data-testid="contact-success-email">{sent.email}</p>
          <p className="mx-auto mt-4 max-w-md text-sm text-[#3a3836]">A unique reference number has been allocated to your submission. You'll find it in your confirmation email.</p>
        </motion.div>
      ) : (
        <motion.form key="form" onSubmit={submit} noValidate data-testid="contact-form" exit={{ opacity: 0, y: -16 }} className="flex flex-col gap-6">
          <Field id="contact-name" label="Full Name" error={errors.full_name}>
            <input id="contact-name" required value={form.full_name} onChange={set("full_name")} placeholder="Your full name" autoComplete="name" data-testid="contact-name-input" className={cls("full_name")} />
          </Field>
          <Field id="contact-email" label="Email" error={errors.email}>
            <input id="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder="name@example.com" autoComplete="email" data-testid="contact-email-input" className={cls("email")} />
          </Field>
          <Field id="contact-inquiry" label="What's your inquiry about?" error={errors.inquiry}>
            <Select value={form.inquiry} onValueChange={set("inquiry")}>
              <SelectTrigger id="contact-inquiry" data-testid="contact-inquiry-select" className={`${cls("inquiry")} h-auto rounded-none`}>
                <SelectValue placeholder="Choose an option" />
              </SelectTrigger>
              <SelectContent className="rounded-none border-[#E62314] bg-[#FBF9F5]">
                {INQUIRY_OPTIONS.map((o) => (
                  <SelectItem key={o} value={o} data-testid={`contact-inquiry-option-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {form.inquiry === "Other" && (
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}>
              <Field id="contact-other" label="Please tell us what it's about" error={errors.other}>
                <input id="contact-other" required value={form.other} onChange={set("other")} placeholder="Type your answer" data-testid="contact-other-input" className={cls("other")} />
              </Field>
            </motion.div>
          )}
          <Field id="contact-message" label="Message" error={errors.message}>
            <textarea id="contact-message" required rows={6} value={form.message} onChange={set("message")} placeholder="Write your message" data-testid="contact-message-input" className={`${cls("message")} resize-y`} />
          </Field>
          <Field id="contact-files" label={`Attach files (optional, up to ${MAX_FILES})`} required={false}>
            <FileDrop files={files} setFiles={setFiles} onError={setError} />
          </Field>
          <Captcha ref={captcha} onChange={setToken} testId="contact-recaptcha" />
          <div className="mt-2">
            <button type="submit" disabled={loading} data-testid="contact-send-link"
              className="group inline-flex items-center gap-2 border-b-2 border-[#E62314] pb-1 text-lg font-extrabold text-black transition-colors hover:text-[#E62314] disabled:opacity-40">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Send
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
          </div>
          {error && <p data-testid="contact-error-message" className="text-sm font-semibold text-[#C21B0E]">{error}</p>}
          <RecaptchaNotice testId="contact-recaptcha-notice" />
        </motion.form>
      )}
    </AnimatePresence>
  );
};
